/**
 * Activity Calendar Component
 *
 * GitHub-style contribution graph showing learning patterns.
 * Shows last 12 weeks of activity with gentle color coding.
 *
 * Philosophy: Celebrate consistency, don't shame gaps.
 */

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ActivityCalendarProps {
  sessions: Array<{
    completedAt: string;
    totalQuestions: number;
  }>;
}

interface DayData {
  date: Date;
  count: number;
  sessions: number;
}

export function ActivityCalendar({ sessions }: ActivityCalendarProps) {
  // Generate last 12 weeks of calendar data
  const calendarData = useMemo(() => {
    const weeks: DayData[][] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Start from 12 weeks ago (84 days)
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 83);

    // Find the previous Sunday to start the week grid properly
    const dayOfWeek = startDate.getDay();
    startDate.setDate(startDate.getDate() - dayOfWeek);

    // Build session map
    const sessionMap = new Map<string, { count: number; sessions: number }>();
    sessions.forEach((session) => {
      const date = new Date(session.completedAt);
      date.setHours(0, 0, 0, 0);
      const key = date.toISOString().split("T")[0] ?? "";
      if (!key) return;

      const existing = sessionMap.get(key) || { count: 0, sessions: 0 };
      sessionMap.set(key, {
        count: existing.count + session.totalQuestions,
        sessions: existing.sessions + 1,
      });
    });

    // Generate 12 weeks
    for (let week = 0; week < 12; week++) {
      const weekDays: DayData[] = [];

      for (let day = 0; day < 7; day++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(currentDate.getDate() + week * 7 + day);

        const key = currentDate.toISOString().split("T")[0] ?? "";
        const data = sessionMap.get(key) || { count: 0, sessions: 0 };

        weekDays.push({
          date: currentDate,
          count: data.count,
          sessions: data.sessions,
        });
      }

      weeks.push(weekDays);
    }

    return weeks;
  }, [sessions]);

  // Calculate max for color scaling
  const maxCount = useMemo(() => {
    return Math.max(
      1,
      ...calendarData.flat().map((d) => d.count)
    );
  }, [calendarData]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Learning Activity</CardTitle>
        <p className="text-sm text-muted-foreground">
          Last 12 weeks • Each square is a day
        </p>
      </CardHeader>
      <CardContent>
        <TooltipProvider delayDuration={0}>
          <div className="flex gap-1">
            {/* Month labels */}
            <div className="flex flex-col justify-end mr-2">
              <div className="text-xs text-muted-foreground mb-1">Mon</div>
              <div className="text-xs text-muted-foreground mb-1">Wed</div>
              <div className="text-xs text-muted-foreground mb-1">Fri</div>
            </div>

            {/* Calendar grid */}
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-1">
                {calendarData.map((week, weekIndex) => (
                  <div key={weekIndex} className="flex flex-col gap-1">
                    {week.map((day, dayIndex) => {
                      const isToday = day.date.toDateString() === new Date().toDateString();
                      const isFuture = day.date > new Date();

                      return (
                        <Tooltip key={dayIndex}>
                          <TooltipTrigger asChild>
                            <div
                              className={`w-3 h-3 rounded-sm transition-colors cursor-pointer ${
                                isFuture
                                  ? "bg-muted/30"
                                  : getActivityColor(day.count, maxCount)
                              } ${
                                isToday
                                  ? "ring-2 ring-primary ring-offset-1"
                                  : ""
                              }`}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <div className="text-xs">
                              <div className="font-semibold">
                                {formatDate(day.date)}
                              </div>
                              {day.count > 0 ? (
                                <>
                                  <div className="text-muted-foreground">
                                    {day.count} question{day.count !== 1 ? "s" : ""} answered
                                  </div>
                                  <div className="text-muted-foreground">
                                    {day.sessions} session{day.sessions !== 1 ? "s" : ""}
                                  </div>
                                </>
                              ) : (
                                <div className="text-muted-foreground">
                                  No activity
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                <span>Less</span>
                <div className="flex gap-1">
                  <div className="w-3 h-3 rounded-sm bg-muted" />
                  <div className="w-3 h-3 rounded-sm bg-green-100 dark:bg-green-950" />
                  <div className="w-3 h-3 rounded-sm bg-green-300 dark:bg-green-800" />
                  <div className="w-3 h-3 rounded-sm bg-green-500 dark:bg-green-600" />
                  <div className="w-3 h-3 rounded-sm bg-green-700 dark:bg-green-400" />
                </div>
                <span>More</span>
              </div>
            </div>
          </div>
        </TooltipProvider>

        {/* Encouraging message */}
        <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <p className="text-sm text-muted-foreground">
            💡 Consistency beats perfection! Even short sessions help build lasting knowledge.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Get color class based on activity level
 * Uses gentle green shades - not punishing red/yellow
 */
function getActivityColor(count: number, max: number): string {
  if (count === 0) return "bg-muted";

  const percentage = count / max;

  if (percentage >= 0.75) {
    return "bg-green-700 dark:bg-green-400 hover:bg-green-600 dark:hover:bg-green-300";
  } else if (percentage >= 0.5) {
    return "bg-green-500 dark:bg-green-600 hover:bg-green-400 dark:hover:bg-green-500";
  } else if (percentage >= 0.25) {
    return "bg-green-300 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700";
  } else {
    return "bg-green-100 dark:bg-green-950 hover:bg-green-50 dark:hover:bg-green-900";
  }
}

/**
 * Format date for tooltip
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
